import React from 'react'
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Autocomplete,
} from '@mui/material'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useSnackbar } from 'notistack'
import { useGetAccounts } from '../../api/user'

const schema = yup.object().shape({
  companyAccountId: yup.string().required('Company is required'),
  promotion1: yup.number().required('Promotion 1 is required').min(0, 'Must be at least 0'),
  promotion2: yup.number().required('Promotion 2 is required').min(0, 'Must be at least 0'),
  ticketDeducted: yup.number().required('Ticket Deducted is required').min(0, 'Must be at least 0'),
})

const LuckyDrawForm = ({ open, setOpen, defaultValues, api, refetch }) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues,
    resolver: yupResolver(schema),
  })

  const { data: accounts = [], isLoading: isLoadingAccounts } = useGetAccounts()
  const { mutateAsync, isLoading } = api()
  const { enqueueSnackbar } = useSnackbar()

  const onSubmit = async (values) => {
    try {
      await mutateAsync(values)
      await refetch()
      if (!defaultValues._id) reset()
      enqueueSnackbar(`Successfully ${values._id ? 'updated' : 'created'}`, { variant: 'success' })
      setOpen(false)
    } catch {
      enqueueSnackbar('Something went wrong. Please try again.', { variant: 'error' })
    }
  }

  return (
    <Dialog maxWidth='sm' open={open} fullWidth>
      <DialogTitle>{defaultValues._id ? 'Edit' : 'Create'} Lucky Draw promotion</DialogTitle>{' '}
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Box mb={2}>
            <Controller
              name='companyAccountId'
              control={control}
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  options={accounts}
                  getOptionLabel={(option) => option.companyName}
                  value={accounts.find((option) => option.companyAccountId === value) || null}
                  onChange={(_, newValue) => {
                    if (!newValue) return
                    setValue('companyName', newValue.companyName)
                    onChange(newValue.companyAccountId)
                  }}
                  disabled={!!defaultValues._id}
                  loading={isLoadingAccounts}
                  disableClearable
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Company'
                      variant='outlined'
                      fullWidth
                      error={!!errors.companyAccountId}
                      helperText={errors.companyAccountId?.message}
                    />
                  )}
                />
              )}
            />
          </Box>
          <Box display='flex' gap={2} mb={2}>
            <Box flex={1}>
              <Controller
                name='promotion1'
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label='Promotion 1'
                    type='number'
                    variant='outlined'
                    fullWidth
                    error={!!errors.promotion1}
                    helperText={errors.promotion1?.message}
                  />
                )}
              />
            </Box>
            <Box flex={1}>
              <Controller
                name='promotion2'
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label='Promotion 2'
                    type='number'
                    variant='outlined'
                    fullWidth
                    error={!!errors.promotion2}
                    helperText={errors.promotion2?.message}
                  />
                )}
              />
            </Box>
            <Box flex={1}>
              <Controller
                name='ticketDeducted'
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label='Ticket Deducted'
                    type='number'
                    variant='outlined'
                    fullWidth
                    error={!!errors.ticketDeducted}
                    helperText={errors.ticketDeducted?.message}
                  />
                )}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant='outlined'
            onClick={() => {
              reset(defaultValues)
              setOpen(false)
            }}
          >
            CANCEL
          </Button>
          <Button type='submit' variant='contained' color='primary' disabled={isLoading}>
            SUBMIT
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default LuckyDrawForm
